import type { OnInit } from '@angular/core';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import type { NonNullableFormBuilder } from '@angular/forms';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import type {
  OnyxCustomAddress,
  OnyxPhone,
  OnyxRoutePoint,
} from '@onyx/angular';
import {
  OnyxAddressInputComponent,
  OnyxCheckboxComponent,
  OnyxFormGroupComponent,
  OnyxIconBoxComponent,
  OnyxIconComponent,
  OnyxInputLabelComponent,
  OnyxSuggestionsComponent,
  onyxTextareaLengthValidator,
  OnyxTextFieldComponent,
  onyxTextFieldLengthValidator,
  OnyxTimepickerComponent,
  OnyxToggleComponent,
} from '@onyx/angular';
import { chain, isEqual, omit, toArray } from 'lodash';
import { map } from 'rxjs';
import { v7 as uuidv7 } from 'uuid';
import { BusinessHoursFormComponent } from '../../../../../common/components/forms/business-hours-form/business-hours-form.component';
import { DAY_TIME_RANGE } from '../../../../../common/constants/common/day-time-range';
import { SuggestionHelper } from '../../../../../common/helpers/suggestions.helper';
import { ValidationHelper } from '../../../../../common/helpers/validation.helper';
import { OrderPointCategory } from '../../../common/enums/order-point-category';
import type { OrderPointTypeByCategory } from '../../../common/enums/order-point-type';
import { OrderPointInformationFormComponent } from '../order-point-form/order-point-information-form/order-point-information-form.component';

export type OrderCheckpointFormGroup = ReturnType<
  typeof OrderCheckpointFormComponent.buildForm
>;

@Component({
  selector: 'app-order-checkpoint-form',
  imports: [
    ReactiveFormsModule,
    OnyxIconBoxComponent,
    OnyxIconComponent,
    TranslatePipe,
    OnyxAddressInputComponent,
    OnyxTextFieldComponent,
    OnyxTimepickerComponent,
    OnyxInputLabelComponent,
    OnyxToggleComponent,
    OnyxFormGroupComponent,
    OnyxSuggestionsComponent,
    OnyxCheckboxComponent,
    OrderPointInformationFormComponent,
  ],
  templateUrl: './order-checkpoint-form.component.html',
  styleUrl: './order-checkpoint-form.component.scss',
})
export class OrderCheckpointFormComponent implements OnInit {
  private readonly injector = inject(Injector);

  public readonly form = input.required<OrderCheckpointFormGroup>();
  public readonly disabled = input.required<boolean>();
  public readonly compact = input(false);

  protected readonly I18N = 'orders.orderForm.points';
  protected readonly TIME_RANGE_SUGGESTIONS =
    SuggestionHelper.getTimeRangeSuggestions();

  protected serviceTimeLabel = computed(
    () => `forms.serviceTime.${this.form().controls.type.value}`,
  );

  protected isAlwaysOpen = signal(false);
  protected isSaturdayClosed = signal(false);
  protected isSundayClosed = signal(false);

  public ngOnInit(): void {
    const controls = this.form().controls;

    if (controls.category.getRawValue() === OrderPointCategory.PICKUP) {
      ValidationHelper.disableControls(controls.address, { reset: false });
    }

    if (this.disabled()) {
      ValidationHelper.disableControls(
        Object.values(omit(controls, 'includeInOrder')),
        { reset: false },
      );
    }

    const businessHoursForm = controls.businessHours;
    const businessHours = toSignal(
      businessHoursForm.valueChanges.pipe(
        map(() => businessHoursForm.getRawValue()),
      ),
      {
        initialValue: businessHoursForm.getRawValue(),
        injector: this.injector,
      },
    );

    this.isAlwaysOpen.set(
      chain(businessHours())
        .toArray()
        .every((value) => isEqual(value, DAY_TIME_RANGE))
        .value(),
    );
    this.isSaturdayClosed.set(businessHours()?.saturday === null);
    this.isSundayClosed.set(businessHours()?.sunday === null);

    effect(
      () => {
        if (this.isSaturdayClosed() || this.isSundayClosed()) {
          this.isAlwaysOpen.set(false);
        }
      },
      { injector: this.injector },
    );

    effect(
      () => {
        if (this.disabled()) return;

        ValidationHelper.toggleControls(businessHoursForm, {
          enabled: !this.isAlwaysOpen(),
          reset: true,
        });

        if (this.isAlwaysOpen()) {
          this.isSaturdayClosed.set(false);
          this.isSundayClosed.set(false);

          const controls = toArray(businessHoursForm.controls);
          for (const control of controls) {
            control.setValue(DAY_TIME_RANGE);
          }
        }

        const weekendControls = new Map([
          [businessHoursForm.controls.saturday, this.isSaturdayClosed()],
          [businessHoursForm.controls.sunday, this.isSundayClosed()],
        ]);
        for (const [control, disabled] of weekendControls) {
          if (disabled) {
            ValidationHelper.disableControls(control, { reset: true });
          }
        }
      },
      { injector: this.injector },
    );
  }

  protected setName(name: string): void {
    this.form().controls.name.setValue(name);
  }

  public static buildForm<
    Category extends OrderPointCategory.CHECKPOINT | OrderPointCategory.PICKUP,
  >(
    fb: NonNullableFormBuilder,
    category: Category,
    type: OrderPointTypeByCategory[Category],
    includeInOrder: boolean,
    isAlwaysOpen: boolean,
  ) {
    return fb.group({
      uuid: fb.control(uuidv7()),
      category: fb.control(category),
      type: fb.control(type),
      includeInOrder: fb.control(includeInOrder),
      address: fb.control<OnyxCustomAddress | null>(null, [
        Validators.required,
      ]),
      name: fb.control<string | null>(null, [onyxTextFieldLengthValidator]),
      serviceTime: fb.control<number | null>(null),
      driverNote: fb.control<string | null>(null, [
        onyxTextareaLengthValidator,
      ]),
      phone: fb.control<OnyxPhone | null>(null),
      businessHours: BusinessHoursFormComponent.buildForm(fb, isAlwaysOpen),
      route: fb.control<OnyxRoutePoint | null>(null),
    });
  }
}
